import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Sparkles, Check, X, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type Route = {
  id: string;
  request_id: string;
  affiliate_id: string;
  score: number;
  services_overlap: string[];
  counties_overlap: string[];
  status: string;
  viewed_at: string | null;
  claimed_at: string | null;
  declined_at: string | null;
  created_at: string;
  idea_match_requests: {
    id: string;
    idea_id: string;
    notes: string | null;
    services_needed: string[];
    counties: string[];
    ideas: { id: string; title: string; category: string | null } | null;
  } | null;
};

export default function RoutedLeadsCard({ affiliateId }: { affiliateId: string }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["partner-match-routes", affiliateId],
    queryFn: async (): Promise<Route[]> => {
      const { data, error } = await supabase
        .from("partner_match_routes")
        .select(
          "id, request_id, affiliate_id, score, services_overlap, counties_overlap, status, viewed_at, claimed_at, declined_at, created_at, idea_match_requests:request_id ( id, idea_id, notes, services_needed, counties, ideas:idea_id ( id, title, category ) )",
        )
        .eq("affiliate_id", affiliateId)
        .order("score", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(25);
      if (error) throw error;
      return (data ?? []) as unknown as Route[];
    },
  });

  const setStatus = async (id: string, status: "claimed" | "declined" | "viewed") => {
    const now = new Date().toISOString();
    const patch = {
      status,
      ...(status === "claimed" ? { claimed_at: now } : {}),
      ...(status === "declined" ? { declined_at: now } : {}),
      ...(status === "viewed" ? { viewed_at: now } : {}),
    };
    const { error } = await supabase.from("partner_match_routes").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(status === "claimed" ? "Claimed — request author notified" : status === "declined" ? "Declined" : "Marked viewed");
    qc.invalidateQueries({ queryKey: ["partner-match-routes", affiliateId] });
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading routed leads…</div>;
  }
  if (!data || data.length === 0) return null;

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-display text-lg font-semibold">Routed to you</h3>
        <Badge variant="outline" className="ml-auto">{data.length}</Badge>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Match requests auto-routed to your org based on services × counties overlap.
      </p>
      <ul className="space-y-3">
        {data.map((r) => {
          const idea = r.idea_match_requests?.ideas;
          return (
            <li key={r.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {idea ? (
                      <Link to={`/ideas/${idea.id}`} className="font-semibold hover:underline inline-flex items-center gap-1">
                        {idea.title} <ExternalLink className="h-3 w-3" />
                      </Link>
                    ) : (
                      <span className="font-semibold text-muted-foreground">Match request</span>
                    )}
                    <Badge variant="secondary" className="capitalize">{r.status}</Badge>
                    <Badge variant="outline">Score {r.score}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  {r.idea_match_requests?.notes ? (
                    <p className="mt-2 text-sm text-foreground/80 line-clamp-3 whitespace-pre-wrap">
                      {r.idea_match_requests.notes}
                    </p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.services_overlap.map((s) => (
                      <span key={`s-${s}`} className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{s}</span>
                    ))}
                    {r.counties_overlap.map((c) => (
                      <span key={`c-${c}`} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{c}</span>
                    ))}
                  </div>
                </div>
                {r.status === "routed" || r.status === "viewed" ? (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "declined")}>
                      <X className="h-4 w-4 mr-1" /> Pass
                    </Button>
                    <Button size="sm" onClick={() => setStatus(r.id, "claimed")}>
                      <Check className="h-4 w-4 mr-1" /> Claim
                    </Button>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}